import { DailyGoalTypeNames, ProgressStatus } from 'bundles/daily-goals/constants';
import type { CompleteItemsProgressDetails, DailyGoal, GoalTypeAndProgressDetails } from 'bundles/daily-goals/types';

export const isGoalCompleted = (goal: DailyGoal): boolean => {
  return goal.progressStatus === ProgressStatus.Completed;
};

export const isAddCareerGoalGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.AddCareerGoal;
};

export const isReturnToCourseraGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.ReturnToCoursera;
};

export const isPracticeWithCoachGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.PracticeWithCoach;
};

export const isCompleteItemsGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.CompleteItems;
};

export const isCompleteAssessmentsGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.CompleteAssessments;
};

export const isCompleteLecturesGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.CompleteLectures;
};

export const isCompleteHandsOnItemsGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.CompleteHandsOnItems;
};

export const isCompletePracticeGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.CompletePractice;
};

export const isCompleteReadingsGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.CompleteReadings;
};

export const isSetUpLearningPlanGoal = (goal: DailyGoal) =>
  goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.SetUpLearningPlan;

export const isCompleteModuleGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.CompleteModule;
};

export const isScore80PercentOnAssignmentGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.Score80PercentOnAssignment;
};

export const isUnlockPersonalizedTrackerGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.UnlockPersonalizedTracker;
};

export const isContinueWeeklyStreakGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.ContinueWeeklyStreak;
};

export const isMakeProgressOnWeeklyStreakTargetGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.MakeProgressOnWeeklyStreakTarget;
};

export const isStartWeeklyStreakGoal = (goal: DailyGoal): boolean => {
  return goal.goalTypeAndProgressDetails.__typename === DailyGoalTypeNames.StartWeeklyStreak;
};

export const isCompleteItemsProgressDetails = (
  goalTypeAndProgressDetails: GoalTypeAndProgressDetails
): goalTypeAndProgressDetails is { progressDetails: CompleteItemsProgressDetails } => {
  if (!('progressDetails' in goalTypeAndProgressDetails)) {
    return false;
  }

  const pd = goalTypeAndProgressDetails.progressDetails;

  return (
    typeof pd === 'object' &&
    pd !== null &&
    (pd as { __typename?: string }).__typename === 'LearnerGoals_CompleteItemsProgressDetails'
  );
};

/**
 * Returns the number of items completed and the target number of items to complete for a complete items goal
 * @param goal - The goal to get the progress data for
 * @returns The number of items completed and the target number of items to complete for a complete items goal,
 * or `undefined` if the goal does not have complete items progress details
 */
export const getCompletedItemProgressDataFromGoal = (goal: DailyGoal) => {
  if (!isCompleteItemsProgressDetails(goal.goalTypeAndProgressDetails)) {
    return undefined;
  }
  const { numberOfCompletedItems, targetNumberOfItemsToComplete } = goal.goalTypeAndProgressDetails.progressDetails;
  return {
    numberOfCompletedItems,
    targetNumberOfItemsToComplete,
    completedItemProgressPercentage:
      targetNumberOfItemsToComplete > 0 ? (numberOfCompletedItems / targetNumberOfItemsToComplete) * 100 : 0,
  };
};

export const isAllDailyGoalsCompleted = (dailyGoals?: Array<DailyGoal>) => {
  if (!dailyGoals) {
    return false;
  }
  return dailyGoals.every((goal) => goal.progressStatus === ProgressStatus.Completed);
};
